import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatMXN, CATEGORIAS, STATUSES, fileToCompressedDataUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Trash2, ArrowLeft, Save, Search, ImagePlus, X, GripVertical } from "lucide-react";
import { toast } from "sonner";

const pickImages = (item) => {
  if (item.imagenes && item.imagenes.length) return item.imagenes;
  if (item.imagen_url) return [item.imagen_url];
  return [];
};

const emptyLine = () => ({ codigo: "", descripcion: "", unidad: "LOTE", cantidad: 1, precio_unitario: 0, imagenes: [] });

export default function QuotationForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [clientes, setClientes] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [defaults, setDefaults] = useState({ notas: [], garantias: [], datos_bancarios: {}, condiciones_pago: "" });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const [form, setForm] = useState({
    cliente_id: null,
    cliente_nombre: "",
    proyecto: "",
    ubicacion: "",
    categoria: "Residencial",
    status: "Borrador",
    iva_porcentaje: 16,
    descuento_monto: 0,
    descuento_mostrar: false,
    vigencia_dias: 15,
    notas: "",
    notas_items: [],
    garantia_items: [],
    condiciones_pago: "",
    datos_bancarios: { titular: "", banco: "", cuenta: "", clabe: "" },
    lineas: [],
  });
  const [customNote, setCustomNote] = useState("");
  const [customGarantia, setCustomGarantia] = useState("");

  useEffect(() => {
    (async () => {
      const [cli, cat, def] = await Promise.all([api.get("/clientes"), api.get("/catalog"), api.get("/defaults")]);
      setClientes(cli.data);
      setCatalog(cat.data);
      setDefaults(def.data);
      if (isEdit) {
        const { data } = await api.get(`/cotizaciones/${id}`);
        setForm({
          cliente_id: data.cliente_id,
          cliente_nombre: data.cliente_nombre,
          proyecto: data.proyecto,
          ubicacion: data.ubicacion || "",
          categoria: data.categoria,
          status: data.status,
          iva_porcentaje: data.iva_porcentaje,
          descuento_monto: data.descuento_monto || 0,
          descuento_mostrar: !!data.descuento_mostrar,
          vigencia_dias: data.vigencia_dias || 15,
          notas: data.notas || "",
          notas_items: data.notas_items || [],
          garantia_items: data.garantia_items || [],
          condiciones_pago: data.condiciones_pago || def.data.condiciones_pago,
          datos_bancarios: data.datos_bancarios || def.data.datos_bancarios,
          lineas: (data.lineas || []).map((l) => ({ ...l, imagenes: pickImages(l) })),
        });
      } else {
        setForm((f) => ({
          ...f,
          notas_items: def.data.notas,
          garantia_items: def.data.garantias,
          condiciones_pago: def.data.condiciones_pago,
          datos_bancarios: def.data.datos_bancarios,
        }));
      }
    })();
  }, [id, isEdit]);

  const subtotal = useMemo(
    () => form.lineas.reduce((sum, l) => sum + Number(l.cantidad || 0) * Number(l.precio_unitario || 0), 0),
    [form.lineas]
  );
  const iva = Math.max(0, subtotal - Number(form.descuento_monto || 0)) * (Number(form.iva_porcentaje || 0) / 100);
  const total = Math.max(0, subtotal - Number(form.descuento_monto || 0)) + iva;

  const setLine = (idx, patch) => {
    const next = [...form.lineas];
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, lineas: next });
  };
  const removeLine = (idx) => setForm({ ...form, lineas: form.lineas.filter((_, i) => i !== idx) });

  // Drag & drop reorder
  const onDragStart = (idx) => (e) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", String(idx)); } catch {}
  };
  const onDragOver = (idx) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverIdx !== idx) setDragOverIdx(idx);
  };
  const onDragLeave = () => setDragOverIdx(null);
  const onDrop = (targetIdx) => (e) => {
    e.preventDefault();
    const source = dragIdx;
    setDragIdx(null);
    setDragOverIdx(null);
    if (source === null || source === targetIdx) return;
    const next = [...form.lineas];
    const [moved] = next.splice(source, 1);
    next.splice(targetIdx, 0, moved);
    setForm({ ...form, lineas: next });
  };
  const onDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };
  const addEmpty = () => setForm({ ...form, lineas: [...form.lineas, emptyLine()] });
  const addFromCatalog = (item) => {
    setForm({
      ...form,
      lineas: [
        ...form.lineas,
        {
          codigo: item.codigo,
          descripcion: item.descripcion,
          unidad: item.unidad,
          cantidad: 1,
          precio_unitario: item.precio_unitario,
          imagenes: pickImages(item),
        },
      ],
    });
    setPickerOpen(false);
    toast.success(`${item.codigo} agregado`);
  };

  const addLineImages = async (idx, files) => {
    if (!files || files.length === 0) return;
    const current = form.lineas[idx].imagenes || [];
    const slots = Math.max(0, 10 - current.length);
    if (slots === 0) { toast.error("Máximo 10 imágenes por partida"); return; }
    const sel = Array.from(files).slice(0, slots);
    const compressed = await Promise.all(sel.map((f) => fileToCompressedDataUrl(f)));
    setLine(idx, { imagenes: [...current, ...compressed] });
  };

  const removeLineImage = (idx, imgIdx) => {
    const next = (form.lineas[idx].imagenes || []).filter((_, i) => i !== imgIdx);
    setLine(idx, { imagenes: next });
  };

  const pickCliente = (idVal) => {
    if (idVal === "__none__") { setForm({ ...form, cliente_id: null }); return; }
    const c = clientes.find((x) => x.id === idVal);
    if (c) setForm({
      ...form,
      cliente_id: c.id,
      cliente_nombre: c.nombre,
      categoria: c.tipo_proyecto || form.categoria,
      ubicacion: form.ubicacion || c.direccion || "",
    });
  };

  const toggleNote = (text) => {
    const present = form.notas_items.includes(text);
    setForm({ ...form, notas_items: present ? form.notas_items.filter((n) => n !== text) : [...form.notas_items, text] });
  };
  const toggleGarantia = (text) => {
    const present = form.garantia_items.includes(text);
    setForm({ ...form, garantia_items: present ? form.garantia_items.filter((n) => n !== text) : [...form.garantia_items, text] });
  };
  const addCustomNote = () => {
    if (!customNote.trim()) return;
    setForm({ ...form, notas_items: [...form.notas_items, customNote.trim()] });
    setCustomNote("");
  };
  const addCustomGarantia = () => {
    if (!customGarantia.trim()) return;
    setForm({ ...form, garantia_items: [...form.garantia_items, customGarantia.trim()] });
    setCustomGarantia("");
  };
  const removeNote = (text) => setForm({ ...form, notas_items: form.notas_items.filter((n) => n !== text) });
  const removeGarantia = (text) => setForm({ ...form, garantia_items: form.garantia_items.filter((n) => n !== text) });

  const save = async () => {
    if (!form.cliente_nombre || !form.proyecto) { toast.error("Cliente y proyecto son obligatorios"); return; }
    if (form.lineas.length === 0) { toast.error("Agrega al menos una partida"); return; }
    try {
      const payload = {
        ...form,
        lineas: form.lineas.map((l) => ({
          ...l,
          cantidad: Number(l.cantidad) || 0,
          precio_unitario: Number(l.precio_unitario) || 0,
          imagenes: l.imagenes || [],
        })),
        iva_porcentaje: Number(form.iva_porcentaje) || 0,
        descuento_monto: Number(form.descuento_monto) || 0,
        descuento_mostrar: !!form.descuento_mostrar,
        vigencia_dias: Number(form.vigencia_dias) || 15,
      };
      let resp;
      if (isEdit) resp = await api.put(`/cotizaciones/${id}`, payload);
      else resp = await api.post("/cotizaciones", payload);
      toast.success("Cotización guardada");
      navigate(`/cotizaciones/${resp.data.id}`);
    } catch (e) {
      toast.error("Error al guardar");
    }
  };

  const filteredCatalog = useMemo(() => {
    const q = pickerSearch.toLowerCase().trim();
    if (!q) return catalog;
    return catalog.filter((c) =>
      (c.codigo || "").toLowerCase().includes(q)
      || (c.descripcion || "").toLowerCase().includes(q)
      || (c.proyecto || "").toLowerCase().includes(q)
      || (c.cliente_nombre || "").toLowerCase().includes(q)
    );
  }, [catalog, pickerSearch]);

  return (
    <div className="space-y-8" data-testid="quotation-form-page">
      <header className="flex items-center justify-between gap-4">
        <button onClick={() => navigate(-1)} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-2" data-testid="form-back">
          <ArrowLeft className="w-4 h-4" /> Volver
        </button>
        <Button onClick={save} className="bg-[#8B5A33] hover:bg-[#6D4525] text-white" data-testid="quote-save">
          <Save className="w-4 h-4 mr-2" /> {isEdit ? "Actualizar" : "Guardar cotización"}
        </Button>
      </header>

      <div>
        <div className="uppercase-label">{isEdit ? "Editar" : "Crear"}</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-1">Presupuesto material y mano de obra</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 border border-border bg-white p-6 rounded-lg">
        <div className="space-y-1.5 md:col-span-2">
          <Label>Cliente (seleccionar de la base)</Label>
          <Select value={form.cliente_id || "__none__"} onValueChange={pickCliente}>
            <SelectTrigger data-testid="form-cliente-select"><SelectValue placeholder="Selecciona cliente del directorio" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— Sin cliente registrado —</SelectItem>
              {clientes
                .filter((c) => c && c.id && c.nombre)
                .map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="form-cliente-nombre" placeholder="O escribe el nombre (At'n)" value={form.cliente_nombre} onChange={(e) => setForm({ ...form, cliente_nombre: e.target.value })} className="mt-2" />
        </div>
        <div className="space-y-1.5"><Label>Proyecto / obra</Label><Input data-testid="form-proyecto" value={form.proyecto} onChange={(e) => setForm({ ...form, proyecto: e.target.value })} placeholder="Ej. Cocina + Mueble TV - Casa Polanco" /></div>
        <div className="space-y-1.5"><Label>Ubicación / dirección</Label><Input data-testid="form-ubicacion" value={form.ubicacion} onChange={(e) => setForm({ ...form, ubicacion: e.target.value })} placeholder="Ej. Playa del Carmen, Quintana Roo" /></div>
        <div className="space-y-1.5"><Label>Categoría</Label>
          <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
            <SelectTrigger data-testid="form-cat-categoria"><SelectValue /></SelectTrigger>
            <SelectContent>{CATEGORIAS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>Estado</Label>
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger data-testid="form-status"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>IVA (%)</Label><Input data-testid="form-iva" type="number" step="0.01" value={form.iva_porcentaje} onChange={(e) => setForm({ ...form, iva_porcentaje: e.target.value })} /></div>
        <div className="space-y-1.5"><Label>Vigencia (días)</Label><Input data-testid="form-vigencia" type="number" value={form.vigencia_dias} onChange={(e) => setForm({ ...form, vigencia_dias: e.target.value })} /></div>
        <div className="space-y-1.5">
          <Label className="flex items-center justify-between">
            <span>Descuento (MXN)</span>
            <label className="flex items-center gap-1.5 text-[11px] normal-case font-normal text-muted-foreground cursor-pointer">
              <input type="checkbox" checked={form.descuento_mostrar} onChange={(e) => setForm({ ...form, descuento_mostrar: e.target.checked })} data-testid="form-descuento-mostrar" />
              Mostrar en PDF
            </label>
          </Label>
          <Input data-testid="form-descuento" type="number" step="0.01" value={form.descuento_monto} onChange={(e) => setForm({ ...form, descuento_monto: e.target.value })} placeholder="0.00" />
        </div>
      </div>

      {/* Lines */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-2xl">Partidas</h2>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setPickerOpen(true)} data-testid="open-catalog-picker"><Search className="w-4 h-4 mr-2" /> Agregar del catálogo</Button>
            <Button variant="outline" onClick={addEmpty} data-testid="add-empty-line"><Plus className="w-4 h-4 mr-2" /> Partida libre</Button>
          </div>
        </div>

        <div className="space-y-3">
          {form.lineas.length === 0 && (
            <div className="border border-dashed border-border rounded-lg p-10 text-center text-muted-foreground text-sm">Sin partidas. Agrega del catálogo o crea libres.</div>
          )}
          {form.lineas.map((l, idx) => {
            const importe = Number(l.cantidad || 0) * Number(l.precio_unitario || 0);
            const isDragging = dragIdx === idx;
            const isDragOver = dragOverIdx === idx && dragIdx !== idx;
            return (
              <div
                key={idx}
                className={`border border-border bg-white rounded-lg p-4 transition-all ${isDragging ? "opacity-40" : ""} ${isDragOver ? "border-[#8B5A33] border-2 shadow-md" : ""}`}
                data-testid={`line-row-${idx}`}
                onDragOver={onDragOver(idx)}
                onDragLeave={onDragLeave}
                onDrop={onDrop(idx)}
                onDragEnd={onDragEnd}
              >
                <div
                  className="flex items-center gap-2 mb-2 -mt-1 cursor-move select-none text-muted-foreground hover:text-[#8B5A33]"
                  draggable={true}
                  onDragStart={onDragStart(idx)}
                  data-testid={`line-drag-${idx}`}
                  title="Arrastra para reordenar"
                >
                  <GripVertical className="w-4 h-4" />
                  <span className="text-[11px] uppercase tracking-wider">Partida {idx + 1}</span>
                </div>
                <div className="grid grid-cols-12 gap-3">
                  <div className="col-span-12 md:col-span-2">
                    <Label className="text-[11px]">Código</Label>
                    <Input value={l.codigo} onChange={(e) => setLine(idx, { codigo: e.target.value })} className="h-9 mt-1" data-testid={`line-codigo-${idx}`} />
                  </div>
                  <div className="col-span-12 md:col-span-7">
                    <Label className="text-[11px]">Descripción</Label>
                    <Textarea rows={2} value={l.descripcion} onChange={(e) => setLine(idx, { descripcion: e.target.value })} className="mt-1 min-h-[40px]" data-testid={`line-desc-${idx}`} />
                  </div>
                  <div className="col-span-4 md:col-span-1">
                    <Label className="text-[11px]">Unid.</Label>
                    <Input value={l.unidad} onChange={(e) => setLine(idx, { unidad: e.target.value })} className="h-9 mt-1" data-testid={`line-unidad-${idx}`} />
                  </div>
                  <div className="col-span-4 md:col-span-1">
                    <Label className="text-[11px]">Cant.</Label>
                    <Input type="number" step="0.01" value={l.cantidad} onChange={(e) => setLine(idx, { cantidad: e.target.value })} className="h-9 mt-1 text-right tabular" data-testid={`line-cant-${idx}`} />
                  </div>
                  <div className="col-span-4 md:col-span-1">
                    <Label className="text-[11px]">P.U.</Label>
                    <Input type="number" step="0.01" value={l.precio_unitario} onChange={(e) => setLine(idx, { precio_unitario: e.target.value })} className="h-9 mt-1 text-right tabular" data-testid={`line-pu-${idx}`} />
                  </div>
                </div>

                {/* Image row with + */}
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <div className="uppercase-label mr-2">Imágenes de referencia</div>
                  {(l.imagenes || []).map((src, imgIdx) => (
                    <div key={imgIdx} className="relative" data-testid={`line-img-${idx}-${imgIdx}`}>
                      <img src={src} className="w-16 h-16 object-cover rounded border border-border" alt={`l${idx}-${imgIdx}`} />
                      <button onClick={() => removeLineImage(idx, imgIdx)} className="absolute -top-1.5 -right-1.5 bg-[#BC4749] text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                  {(l.imagenes || []).length < 10 && (
                    <label className="cursor-pointer">
                      <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => addLineImages(idx, e.target.files)} data-testid={`line-add-img-${idx}`} />
                      <span className="w-16 h-16 rounded border-2 border-dashed border-[#8B5A33] flex items-center justify-center text-[#8B5A33] hover:bg-[#8B5A33]/10" title="Agregar imágenes">
                        <ImagePlus className="w-5 h-5" />
                      </span>
                    </label>
                  )}
                  <span className="text-[11px] text-muted-foreground ml-1">{(l.imagenes || []).length}/10</span>
                </div>

                <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
                  <span className="text-xs text-muted-foreground">Importe: <span className="tabular font-medium text-foreground" data-testid={`line-importe-${idx}`}>{formatMXN(importe)}</span></span>
                  <Button size="sm" variant="ghost" onClick={() => removeLine(idx)} className="text-[#BC4749]" data-testid={`line-remove-${idx}`}><Trash2 className="w-4 h-4 mr-1" /> Quitar partida</Button>
                </div>
              </div>
            );
          })}
        </div>

        {form.lineas.length > 0 && (
          <div className="mt-4 border border-border bg-white rounded-lg p-4">
            <div className="flex justify-between py-1 text-sm"><span className="uppercase-label">Subtotal</span><span className="tabular" data-testid="form-subtotal">{formatMXN(subtotal)}</span></div>
            {Number(form.descuento_monto) > 0 && (
              <div className="flex justify-between py-1 text-sm text-[#BC4749]"><span className="uppercase-label">Descuento</span><span className="tabular">- {formatMXN(form.descuento_monto)}</span></div>
            )}
            <div className="flex justify-between py-1 text-sm"><span className="uppercase-label">IVA {form.iva_porcentaje}%</span><span className="tabular" data-testid="form-iva-amount">{formatMXN(iva)}</span></div>
            <div className="flex justify-between py-2 border-t border-border mt-1"><span className="uppercase-label">Total</span><span className="font-display text-2xl tabular" data-testid="form-total">{formatMXN(total)}</span></div>
          </div>
        )}
      </section>

      {/* Notas */}
      <section className="border border-border bg-white rounded-lg p-5">
        <h3 className="font-display text-2xl mb-2">Notas del presupuesto</h3>
        <p className="text-xs text-muted-foreground mb-3">Selecciona las que aplican a este proyecto. Puedes agregar notas personalizadas.</p>
        <div className="space-y-2 max-h-72 overflow-y-auto pr-2">
          {defaults.notas.map((n) => {
            const checked = form.notas_items.includes(n);
            return (
              <label key={n} className="flex items-start gap-2 text-sm cursor-pointer" data-testid={`note-toggle-${n.substring(0, 30)}`}>
                <Checkbox checked={checked} onCheckedChange={() => toggleNote(n)} className="mt-0.5" />
                <span className={checked ? "text-foreground" : "text-muted-foreground"}>{n}</span>
              </label>
            );
          })}
          {form.notas_items.filter((n) => !defaults.notas.includes(n)).map((n) => (
            <div key={n} className="flex items-start gap-2 text-sm bg-[#8B5A33]/5 border border-[#8B5A33]/30 rounded px-2 py-1.5">
              <span className="flex-1">{n}</span>
              <button onClick={() => removeNote(n)} aria-label="quitar"><X className="w-3.5 h-3.5 text-[#BC4749]" /></button>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-3">
          <Input value={customNote} onChange={(e) => setCustomNote(e.target.value)} placeholder="Agregar nota personalizada" data-testid="custom-note-input" />
          <Button variant="outline" onClick={addCustomNote} data-testid="custom-note-add">Agregar</Button>
        </div>
      </section>

      {/* Garantías */}
      <section className="border border-border bg-white rounded-lg p-5">
        <h3 className="font-display text-2xl mb-2">Garantía</h3>
        <div className="space-y-2">
          {defaults.garantias.map((g) => {
            const checked = form.garantia_items.includes(g);
            return (
              <label key={g} className="flex items-start gap-2 text-sm cursor-pointer" data-testid={`garantia-toggle-${g.substring(0, 30)}`}>
                <Checkbox checked={checked} onCheckedChange={() => toggleGarantia(g)} className="mt-0.5" />
                <span className={checked ? "text-foreground" : "text-muted-foreground"}>{g}</span>
              </label>
            );
          })}
          {form.garantia_items.filter((g) => !defaults.garantias.includes(g)).map((g) => (
            <div key={g} className="flex items-start gap-2 text-sm bg-[#8B5A33]/5 border border-[#8B5A33]/30 rounded px-2 py-1.5">
              <span className="flex-1">{g}</span>
              <button onClick={() => removeGarantia(g)} aria-label="quitar"><X className="w-3.5 h-3.5 text-[#BC4749]" /></button>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-3">
          <Input value={customGarantia} onChange={(e) => setCustomGarantia(e.target.value)} placeholder="Agregar garantía personalizada" data-testid="custom-garantia-input" />
          <Button variant="outline" onClick={addCustomGarantia} data-testid="custom-garantia-add">Agregar</Button>
        </div>
      </section>

      {/* Condiciones de pago y bancarios */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="border border-border bg-white rounded-lg p-5 space-y-2">
          <h3 className="font-display text-xl">Condiciones de pago</h3>
          <Textarea rows={3} value={form.condiciones_pago} onChange={(e) => setForm({ ...form, condiciones_pago: e.target.value })} data-testid="form-condiciones" />
        </div>
        <div className="border border-border bg-white rounded-lg p-5 space-y-3">
          <h3 className="font-display text-xl">Datos para transferencia</h3>
          <div className="space-y-2">
            <div><Label className="text-[11px]">Titular</Label><Input value={form.datos_bancarios.titular || ""} onChange={(e) => setForm({ ...form, datos_bancarios: { ...form.datos_bancarios, titular: e.target.value } })} data-testid="bank-titular" /></div>
            <div><Label className="text-[11px]">Banco</Label><Input value={form.datos_bancarios.banco || ""} onChange={(e) => setForm({ ...form, datos_bancarios: { ...form.datos_bancarios, banco: e.target.value } })} data-testid="bank-banco" /></div>
            <div><Label className="text-[11px]">Cuenta</Label><Input value={form.datos_bancarios.cuenta || ""} onChange={(e) => setForm({ ...form, datos_bancarios: { ...form.datos_bancarios, cuenta: e.target.value } })} data-testid="bank-cuenta" /></div>
            <div><Label className="text-[11px]">CLABE interbancaria</Label><Input value={form.datos_bancarios.clabe || ""} onChange={(e) => setForm({ ...form, datos_bancarios: { ...form.datos_bancarios, clabe: e.target.value } })} data-testid="bank-clabe" /></div>
          </div>
        </div>
      </section>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-3xl bg-white" data-testid="catalog-picker-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Catálogo de partidas</DialogTitle></DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input className="pl-9" placeholder="Buscar por código, descripción, cliente o proyecto…" value={pickerSearch} onChange={(e) => setPickerSearch(e.target.value)} data-testid="picker-search" />
          </div>
          <div className="max-h-[420px] overflow-y-auto border border-border rounded">
            {filteredCatalog.length === 0 && <div className="p-6 text-center text-muted-foreground text-sm">Sin resultados.</div>}
            {filteredCatalog.map((it) => (
              <button key={it.id} onClick={() => addFromCatalog(it)} data-testid={`picker-item-${it.codigo}`} className="w-full flex items-center gap-4 p-3 hover:bg-[#FAFAF7] border-b border-border text-left">
                {(it.imagenes?.[0] || it.imagen_url) ? (
                  <img src={it.imagenes?.[0] || it.imagen_url} className="w-14 h-14 rounded object-cover border border-border" alt={it.codigo} />
                ) : (
                  <div className="w-14 h-14 rounded bg-[#F4F2EC] border border-border" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium tabular">{it.codigo}</span>
                    <span className="uppercase-label">{it.categoria}</span>
                    {it.cliente_nombre && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#8B5A33]/10 text-[#6D4525] border border-[#8B5A33]/30">{it.cliente_nombre}</span>}
                    {it.proyecto && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#4A7C59]/10 text-[#3a6347] border border-[#4A7C59]/30">{it.proyecto}</span>}
                  </div>
                  <div className="text-xs text-muted-foreground line-clamp-2">{it.descripcion}</div>
                </div>
                <div className="text-right tabular text-sm">{formatMXN(it.precio_unitario)}</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
